import { useState, useCallback, useEffect, useRef, ChangeEvent } from 'react'
import moment from 'moment-timezone'

import { dateFormat as dateFormatDefault } from './date-format'
import type { FormatValues } from './format-values'


type Options = {
  id?: string
  valueIncoming?: string
  defaultValue?: string
  dateFormat?: string
  onChange?: (value: string, id?: string) => void
  disableClearInvalidDate?: boolean
}

export default function usePlainInputDate({
  id,
  valueIncoming,
  defaultValue,
  dateFormat,
  onChange,
  disableClearInvalidDate,
}: Options): [
    string,
    (values: FormatValues) => void,
    (e: ChangeEvent<HTMLInputElement>) => void
  ] {
  const firstRender = useRef(true)

  const [currentValue, setCurrentValue] = useState<string>(() => {
    const mDeaultValue = moment(defaultValue || valueIncoming, dateFormat, true)
    return disableClearInvalidDate || mDeaultValue.isValid() ? mDeaultValue.format(dateFormatDefault) : ''
  })

  const setCurrentValueOnlyValid = useCallback((val: string, format: string) => {
    const mVal = moment(val, format, true)
    setCurrentValue(disableClearInvalidDate || mVal.isValid() ? mVal.format(dateFormatDefault) : '')
  }, [disableClearInvalidDate])

  const currentValueMustBeValid = useCallback(() => {
    setCurrentValue((prev) => {
      const mPrev = moment(prev, dateFormatDefault, true)
      return disableClearInvalidDate || mPrev.isValid() ? prev : ''
    })
  }, [disableClearInvalidDate])

  useEffect(() => {
    if (valueIncoming && dateFormat && !firstRender.current) {
      setCurrentValue(moment(valueIncoming, dateFormat).format(dateFormatDefault))
    }
    firstRender.current = false
  }, [valueIncoming, dateFormat])

  const handleDateValueChange = useCallback((values: FormatValues) => {
    const { formattedValue, dayFormat, valid } = values
    if (onChange) {
      const outgoingValue = valid ? moment(formattedValue, dayFormat).format(dateFormat) : ''
      onChange(outgoingValue, id)
    }
    setCurrentValue(formattedValue)
  }, [setCurrentValue, onChange, dateFormat, id])

  const handleBlur = useCallback(() => {
    if (valueIncoming && dateFormat) {
      setCurrentValueOnlyValid(valueIncoming, dateFormat)
      return
    }
    currentValueMustBeValid()
  }, [valueIncoming, dateFormat, setCurrentValueOnlyValid, currentValueMustBeValid])

  return [currentValue, handleDateValueChange, handleBlur]
}
